// Taki Listing Builder — tedarikci katalogundaki bir urunu yayina hazir
// taslaga cevirir. AYNI akis iki pazaryerinde de calisir; farkliliklar
// aktif magazanin platformundan gelir:
//
//   Trendyol : kategori + zorunlu ozellikler, TRY, urun adi <=100, etiket yok
//   Etsy     : taksonomi, USD, baslik <=140, 13 etiket
//
// Tekstil hattindan farki: tasarim/varyasyon uretimi yok. Urun tedarikciden
// HAZIR gelir; is yuku baslik + aciklama + gerekli alanlar + konsept
// fotograflari + fiyatlandirmadir.
//
// Adimlar tek ekranda: 1) urun  2) sablon  3) icerik  4) gorseller  5) fiyat.
// Kaydetme mevcut listing_drafts uzerinden; pazaryerine ozel alanlar
// draft.attributes altinda durur (ayri tablo gerekmiyor).

import { useEffect, useMemo, useState } from "react";
import { EtsyListingPreviewBox } from "../components/listing/EtsyListingPreview";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, Save, Sparkles, ArrowLeft, Image as ImageIcon,
  AlertTriangle, Check, Tag, Package, X,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { getBackendUrl, authHeaders } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import type { CustomMockup, ProductCategory, SupplierCatalog } from "@erc/shared";
import { titleWarnings } from "@erc/shared";
import { JEWELRY_SHOTS_KEY } from "./JewelryStudioPage";
import {
  TITLE_LIMITS, COMMISSION_DEFAULTS, cleanSupplierHtml, applyPattern,
  productCost, productShipping, DEFAULT_ETSY_SHIPPING_USD, resolveOptionSets, effectiveOptionRefs, attributeLines, productMaterials, buildVariations as buildVariationsLib,
  buildPersonalization as buildPersonalizationLib, etsyLimitError, renderDescriptionFooter, trailingImages, DEFAULT_PRICE_MULTIPLIER, LIST_PRICE_FACTOR, entryComboPrices, scaleVariationPrices,
  DEFAULT_TARGET_MARGIN, marginRule, multiplierRule, etsyProfit, readLastMargin, writeLastMargin,
  type TrendyolAttr, type TemplateRow, type PlannedOption, type PlannedVariation,
  type OptionSet, type OptionRef, soleLeadingInspiration,
} from "../lib/jewelryListing";
import { useTaxonomyProps, baseProps, effectiveTemplateDefaults } from "../lib/etsyProperties";

const abs = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

/** Konsept koleksiyonu: bir urun grubunun (kolye, yuzuk...) sahne seti */
interface ConceptCollection {
  id: string;
  name: string;
  productTypeKey?: string | null;
  mockups: Array<{ id: string }>;
}

/** Kategori ozelliginin builder tarafindan doldurulacagi kaynak */
type AttrSource = "renk" | "beden" | "materyal";
type SourceMap = Record<string, { source: AttrSource; name: string; allowCustom: boolean; varianter: boolean }>;

interface CategoryAttribute {
  id: number | null;
  name: string;
  required: boolean;
  allowCustom: boolean;
  varianter: boolean;
  values: Array<{ id: number | null; name: string }>;
}

const norm = (s: string) =>
  s.toLowerCase()
    .replace(/ı/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
    .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]/g, "");

/** Tedarikci renk adi ↔ Trendyol renk listesi karsiliklari */
const COLOR_ALIASES: Record<string, string[]> = {
  gold: ["sari", "altin", "gold", "goldrengi"],
  silver: ["gumus", "silver", "gri"],
  rose: ["rose", "rosegold", "pembe", "bakir"],
  black: ["siyah"],
  white: ["beyaz"],
};

/** Bir metni kategori deger listesine esler; bulunamazsa null.
 *  Parcali eslesme yalnizca 3+ karakterli degerlerde denenir — aksi halde
 *  "12 inches" adayi "2" adli bedene esleniyordu. */
function matchValue(values: CategoryAttribute["values"], candidate: string): number | null {
  if (!candidate) return null;
  const c = norm(candidate);
  const exact = values.find((v) => norm(v.name) === c);
  if (exact?.id != null) return exact.id;
  const partial = values.find((v) => {
    const n = norm(v.name);
    if (n.length < 3 || c.length < 3) return false;
    return n.includes(c) || c.includes(n);
  });
  if (partial?.id != null) return partial.id;
  for (const [key, aliases] of Object.entries(COLOR_ALIASES)) {
    if (c !== key && !c.includes(key)) continue;
    const hit = values.find((v) => aliases.some((a) => norm(v.name).includes(a)));
    if (hit?.id != null) return hit.id;
  }
  return null;
}

/** Yalnizca birebir ayni olan degeri bulur. Varsayilan beden ararken parcali
 *  eslesme "Standart" adayini "Battal Standart"a baglıyordu. */
function exactValue(values: CategoryAttribute["values"], candidate: string): number | null {
  const c = norm(candidate);
  const hit = values.find((v) => norm(v.name) === c);
  return hit?.id ?? null;
}

export default function JewelryListingBuilderPage() {
  const [params] = useSearchParams();
  const catalogId = params.get("catalogId");
  const categoryId = params.get("categoryId");
  const { vertical, platform } = useShopVertical();
  // Pazaryeri aktif magazadan gelir: ayni katalog urunu Etsy'de de
  // Trendyol'da da listelenebiliyor
  const isEtsy = platform === "etsy";
  const marketplace = isEtsy ? "etsy" : "trendyol";
  const titleMax = TITLE_LIMITS[marketplace] ?? 100;
  const currency = isEtsy ? "USD" : "TRY";

  const [catalog, setCatalog] = useState<SupplierCatalog | null>(null);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [templateId, setTemplateId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [attrs, setAttrs] = useState<TrendyolAttr[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(100);
  // Trendyol: (maliyet + kargo) x carpan. Etsy: tum ucretlerden sonra hedef net kar %
  const [multiplier, setMultiplier] = useState(DEFAULT_PRICE_MULTIPLIER);
  const [marginPct, setMarginPct] = useState(() => readLastMargin() ?? DEFAULT_TARGET_MARGIN);
  const [commission, setCommission] = useState(21);
  const [tags, setTags] = useState<string[]>([]);
  /** AI'nin urune gore cikardigi Etsy malzemeleri (yoksa urun ozelliginden) */
  const [aiMaterials, setAiMaterials] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  // AI icerik uretimi
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStatus, setAiStatus] = useState("");
  const [aiExtra, setAiExtra] = useState("");

  // Konsept fotograflari: sahne sablonlari. Secilenler urunle birlestirilip
  // listenin gorselleri haline gelir.
  const [concepts, setConcepts] = useState<CustomMockup[]>([]);
  const [pickedConcepts, setPickedConcepts] = useState<string[]>([]);
  // Konsept koleksiyonlari: tek secimle bir sahne setini isaretlemek icin
  const [conceptCollections, setConceptCollections] = useState<ConceptCollection[]>([]);
  // Secenek kutuphanesi — sablonun optionRefs'i bunlarla cozulur
  const [optionSets, setOptionSets] = useState<OptionSet[]>([]);
  const [optionPresets, setOptionPresets] = useState<Array<{ id: string; name: string; refs: OptionRef[] }>>([]);
  const [shotBusy, setShotBusy] = useState(false);
  const [shotLog, setShotLog] = useState<string>("");

  // Sablonun "urunden gelsin" dedigi alanlar icin kategori deger listesi
  const [catAttrs, setCatAttrs] = useState<CategoryAttribute[]>([]);
  const [catAttrsLoading, setCatAttrsLoading] = useState(false);
  // Renk basina secilen Trendyol renk degeri ve tek seferlik beden degeri
  const [colorValues, setColorValues] = useState<Record<string, number | "">>({});
  const [sizeValue, setSizeValue] = useState<string>("");

  const product: ProductCategory | null = useMemo(() => {
    const cats = (catalog?.categories ?? []) as ProductCategory[];
    return cats.find((c) => c.id === categoryId) ?? null;
  }, [catalog, categoryId]);

  // Maliyet/kargo: pazaryerinin ulkesindeki fiyat (ortak kural)
  const cost = useMemo(() => productCost(product, isEtsy), [product, isEtsy]);
  const productShip = useMemo(() => productShipping(product, isEtsy), [product, isEtsy]);
  // Kargo burada da girilebilir; odak cikinca URUNE kaydedilir (katalogdaki alanla ayni)
  const [shipEdit, setShipEdit] = useState<string | null>(null);
  useEffect(() => { setShipEdit(null); }, [product?.id]);
  const shipping = shipEdit !== null && shipEdit.trim() !== "" && Number.isFinite(Number(shipEdit.replace(",", ".")))
    ? Number(shipEdit.replace(",", "."))
    : productShip;
  async function saveShipping() {
    if (!isEtsy || !catalogId || !categoryId || shipEdit === null) return;
    const v = shipEdit.trim() === "" ? null : Number(shipEdit.replace(",", "."));
    try {
      await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${categoryId}/shipping-cost`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ usd: v }),
      });
    } catch { /* kaydedilemezse bu liste icin girilen deger yine kullanilir */ }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [cat, tplRes, conceptList, collectionList, optionRes] = await Promise.all([
          catalogId ? apiClient.getSupplierCatalog(catalogId) : Promise.resolve(null),
          fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() }).then((r) => r.json()),
          apiClient.listMockups("concept").catch(() => [] as CustomMockup[]),
          apiClient.listMockupCollections("concept").catch(() => []),
          fetch(`${getBackendUrl()}/api/option-sets`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : { data: [] })).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCatalog(cat);
        setConcepts(conceptList ?? []);
        setConceptCollections((collectionList ?? []) as unknown as ConceptCollection[]);
        setOptionSets(((optionRes as { data?: OptionSet[] })?.data ?? []) as OptionSet[]);
        // Kaliplar: tek secimle secenek kumesi uygulamak icin
        fetch(`${getBackendUrl()}/api/option-sets/presets/list`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : { data: [] }))
          .then((b) => setOptionPresets((b?.data ?? []) as Array<{ id: string; name: string; refs: OptionRef[] }>))
          .catch(() => {});
        // Sunucu sablonlari zaten aktif magazanin pazaryerine gore donduruyor.
        // Trendyol'da ek kosul var: kategori secilmemis sablon builder'i
        // dolduramaz (zorunlu ozellikler oradan geliyor).
        const rows = (tplRes?.data ?? []) as TemplateRow[];
        setTemplates(platform === "trendyol" ? rows.filter((t) => t.trendyolCategoryId != null) : rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Veriler alınamadı");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [catalogId, platform]);

  // Komisyon varsayilani pazaryerine gore (kullanici uzerine yazabilir)
  useEffect(() => {
    setCommission(COMMISSION_DEFAULTS[marketplace] ?? 21);
  }, [marketplace]);

  // Urun yuklenince icerigi on doldur (sablon secilmemisken de calisir)
  useEffect(() => {
    if (!product) return;
    const src = product.source;
    setTitle((prev) => prev || product.name.slice(0, titleMax));
    setDescription((prev) => prev || (src?.descriptionHtml ? cleanSupplierHtml(src.descriptionHtml) : src?.descriptionText || ""));
    setColors(product.colorAvailability?.map((c) => c.color) ?? []);
    setImages(src?.images?.slice(0, 8) ?? []);
  }, [product]);

  // Studio'dan gelen konsept gorselleri (Katalog → Studio → Listing Builder).
  // Ayni urun icinse listenin BASINA eklenir: uretilen sahne kapak gorselidir.
  useEffect(() => {
    if (!product || !categoryId) return;
    let payload: { categoryId?: string; images?: string[] } | null = null;
    try {
      const raw = sessionStorage.getItem(JEWELRY_SHOTS_KEY);
      if (raw) payload = JSON.parse(raw) as { categoryId?: string; images?: string[] };
      sessionStorage.removeItem(JEWELRY_SHOTS_KEY);
    } catch { /* sessionStorage kapali — gorseller burada yeniden uretilebilir */ }
    const shots = payload?.categoryId === categoryId ? payload?.images ?? [] : [];
    if (shots.length) setImages((prev) => [...shots, ...prev.filter((u) => !shots.includes(u))].slice(0, 10));

    // Urunun galerisindeki uretilmis gorseller (ana gorsel basta) listeye eklenir
    (async () => {
      try {
        const r = await fetch(
          `${getBackendUrl()}/api/product-shots?catalogId=${catalogId}&categoryId=${categoryId}`,
          { headers: authHeaders() },
        );
        const b = await r.json().catch(() => ({}));
        if (!r.ok) return;
        const urls = ((b.data ?? []) as Array<{ imageUrl: string }>).map((x) => x.imageUrl);
        if (urls.length) setImages((prev) => [...urls, ...prev.filter((u) => !urls.includes(u))].slice(0, 10));
      } catch { /* galeri alinamadi — tedarikci gorselleriyle devam */ }
    })();
  }, [product, categoryId, catalogId]);

  const template = templates.find((t) => t.id === templateId) ?? null;
  // Etsy kategori ozellikleri: sablonun kategorisinden, kural + sablon varsayilani
  // (AI ile tamamlama Liste Denetimi / Listing Builder'da)
  // Sablonun kategorisi kullanilmaz: takı sablonlarinda Charm Necklaces, bileklik oraya dusuyordu
  const etsyTaxonomyId = soleLeadingInspiration(product?.inspirations ?? [])?.taxonomyId ?? null;
  const { data: templateTaxProps } = useTaxonomyProps(isEtsy ? etsyTaxonomyId : null);
  const sourceMap: SourceMap = template?.attributes?.attributeSources ?? {};

  // Sablonda "urunden gelsin" isaretlenen alanlarin deger listesi kategoriden
  // gelir (Web Color gibi sabit listeli alanlarda eslesme gerekiyor).
  useEffect(() => {
    const catId = template?.trendyolCategoryId;
    if (!catId) { setCatAttrs([]); return; }
    let cancelled = false;
    (async () => {
      setCatAttrsLoading(true);
      try {
        const r = await fetch(`${getBackendUrl()}/api/trendyol/categories/${catId}/attributes`, { headers: authHeaders() });
        const b = await r.json().catch(() => ({}));
        if (!cancelled) setCatAttrs((b?.data?.attributes ?? []) as CategoryAttribute[]);
      } catch { if (!cancelled) setCatAttrs([]); }
      if (!cancelled) setCatAttrsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [template?.trendyolCategoryId, templateId]);

  // Urun degerlerini kategori listesine esle: renk basina bir deger, beden tek
  const colorAttrs = catAttrs.filter((a) => sourceMap[String(a.id)]?.source === "renk");
  const sizeAttr = catAttrs.find((a) => sourceMap[String(a.id)]?.source === "beden") ?? null;
  const materialAttr = catAttrs.find((a) => sourceMap[String(a.id)]?.source === "materyal") ?? null;
  const materialText = product?.source?.attributes?.["Materyal"] ?? product?.source?.attributes?.["Material"] ?? "";

  /** Beden adayi: gercek beden ekseni varsa o, yoksa zincir uzunlugu, o da
   *  yoksa "Standart" — takida beden yok ama Trendyol alani zorunlu tutuyor. */
  const sizeCandidates = useMemo(() => {
    const real = (product?.sizeOptions ?? []).map((s) => s.size).filter((x) => x && x !== "Standart");
    if (real.length) return real;
    const chain = product?.source?.attributes?.["Zincir Uzunluğu"] ?? product?.source?.attributes?.["Zincir Uzunlugu"];
    return chain ? [chain] : ["Standart"];
  }, [product]);

  // Listeler geldiginde otomatik eslesme yap (kullanici degistirebilir)
  useEffect(() => {
    const listAttr = colorAttrs.find((a) => a.values.length > 0);
    if (listAttr) {
      setColorValues((prev) => {
        const next = { ...prev };
        for (const c of colors) if (next[c] == null) next[c] = matchValue(listAttr.values, c) ?? "";
        return next;
      });
    }
    if (sizeAttr && !sizeValue) {
      // Once urunun kendi degeri, sonra birebir "Standart / Tek Ebat / Tek
      // Beden". Hicbiri yoksa BOS birakilir: yanlis beden gondermektense
      // kullaniciya sectirmek dogru.
      const hit = sizeCandidates.map((c) => matchValue(sizeAttr.values, c)).find((v) => v != null);
      const std = ["Standart", "Tek Ebat", "Tek Beden"]
        .map((n) => exactValue(sizeAttr.values, n)).find((v) => v != null);
      setSizeValue(hit != null || std != null ? String(hit ?? std) : "");
    }
  }, [catAttrs, colors, sizeCandidates]);

  /** Bir rengin Trendyol ozellik degerleri — renk basina ayri varyant satiri */
  function attrsForColor(color: string): TrendyolAttr[] {
    const out: TrendyolAttr[] = [...attrs];
    for (const a of colorAttrs) {
      if (a.id == null) continue;
      if (a.values.length > 0) {
        const v = colorValues[color];
        if (v !== "" && v != null) out.push({ attributeId: a.id, attributeName: a.name, attributeValueId: Number(v) });
      } else if (a.allowCustom) {
        out.push({ attributeId: a.id, attributeName: a.name, customAttributeValue: color });
      }
    }
    if (sizeAttr?.id != null && sizeValue) {
      out.push({ attributeId: sizeAttr.id, attributeName: sizeAttr.name, attributeValueId: Number(sizeValue) });
    }
    if (materialAttr?.id != null && materialText) {
      const v = matchValue(materialAttr.values, materialText);
      out.push(v != null
        ? { attributeId: materialAttr.id, attributeName: materialAttr.name, attributeValueId: v }
        : { attributeId: materialAttr.id, attributeName: materialAttr.name, customAttributeValue: materialText });
    }
    return out;
  }

  // Eslesmemis zorunlu alanlar: renk listesi bos kalan renkler + beden
  const unmatchedColors = colorAttrs.some((a) => a.values.length > 0)
    ? colors.filter((c) => colorValues[c] === "" || colorValues[c] == null)
    : [];
  const sizeMissing = !!sizeAttr && !sizeValue;

  /** Sablonu uygula: kategori ozellikleri + baslik/aciklama kalibi + fiyat kurali */
  function applyTemplate(t: TemplateRow) {
    const src = product?.source;
    const vars: Record<string, string> = {
      urun: product?.name ?? "",
      marka: product?.brand ?? "",
      sku: src?.sku ?? "",
      materyal: src?.attributes?.["Materyal"] ?? src?.attributes?.["Material"] ?? "",
      renk: colors.join(", "),
      ozellikler: attributeLines(product),
    };
    const rules = t.attributes ?? {};
    if (rules.titlePattern) setTitle(applyPattern(rules.titlePattern, vars).slice(0, titleMax));
    if (rules.descriptionPattern) {
      setDescription(applyPattern(rules.descriptionPattern, { ...vars, aciklama: src?.descriptionText ?? "" }));
    } else if (t.description) {
      setDescription((prev) => prev || t.description);
    }
    if (rules.priceMultiplier) setMultiplier(rules.priceMultiplier);
    // Tarif alanlari sablon kurallarindan ONCELIKLI: dogrudan sablonun kendi alani
    if (t.priceMultiplier) setMultiplier(t.priceMultiplier);
    // Tarifteki konsept koleksiyonu = uretilecek sahneler; tek secimle isaretlenir
    if (t.conceptCollectionId) {
      const col = conceptCollections.find((c) => c.id === t.conceptCollectionId);
      if (col) {
        const ids = col.mockups.map((m) => m.id).filter((id) => concepts.some((c) => c.id === id));
        if (ids.length) setPickedConcepts(ids);
      }
    }
    if (rules.commissionPercent != null) setCommission(rules.commissionPercent);
    setMarginPct(readLastMargin() ?? rules.targetMarginPct ?? DEFAULT_TARGET_MARGIN);
    if (t.quantity) setQuantity(t.quantity);
    setAttrs(t.trendyolAttributes ?? []);
  }

  // Fiyat kurali: Etsy hedef kardan (tum Etsy ucretleri dusulur), Trendyol carpandan
  const rule = isEtsy ? marginRule(marginPct) : multiplierRule(multiplier);
  const salePrice = rule.price(cost + shipping);
  const etsyAnalysis = isEtsy ? etsyProfit(salePrice, cost, shipping) : null;
  const profit = etsyAnalysis ? etsyAnalysis.net : salePrice - cost - shipping - (salePrice * commission) / 100;
  const margin = salePrice > 0 ? (profit / salePrice) * 100 : 0;

  const missing: string[] = [];
  if (!title.trim()) missing.push("başlık");
  if (!description.trim()) missing.push("açıklama");
  // Trendyol'da kategori ve zorunlu ozellikler sablondan gelir; Etsy'de
  // sablon opsiyonel (taksonomi taslakta sonradan da secilebiliyor)
  if (!template && !isEtsy) missing.push("şablon (Trendyol kategorisi)");
  if (images.length === 0) missing.push("görsel");
  if (!isEtsy && unmatchedColors.length) missing.push(`renk eşleşmesi (${unmatchedColors.join(", ")})`);
  if (!isEtsy && sizeMissing) missing.push("beden");
  if (isEtsy && tags.length === 0) missing.push("etiket");
  if (isEtsy && !etsyTaxonomyId) missing.push("Etsy kategorisi (ilham listesi yok ya da kategorileri eşit: Liste Denetimi'nde seçin)");

  /** Baslik + aciklamayi AI ile uret. Kaynak: tedarikci aciklamasi ve sabit
   *  ozellikler; sablon kaliplari varsa onlar zaten uygulanmis olur ve
   *  kullanici uretimi ancak isterse calistirir (uzerine yazar). */
  async function generateContent() {
    if (!product) return;
    setAiBusy(true); setError(null);
    try {
      // Tedarikcinin Turkce fiyat ekseni ("Zincir Tipi + Doğum Taşı Sayısı") Etsy'ye gitmesin
      if (isEtsy) {
        const ax = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${categoryId}/english-axis`, { method: "POST", headers: authHeaders() });
        const axb = await ax.json().catch(() => ({}));
        if (!ax.ok) throw new Error(axb.error || "Varyasyon adları çevrilemedi");
        if (axb.changed) setCatalog((c) => c && ({ ...c, categories: (c.categories as ProductCategory[]).map((x) => x.id === product.id ? axb.category : x) }) as typeof c);
      }
      // Yeni urunde havuz yok: once Etsy API aramasi + ilham listesi kurulur (otomatik hattin ilk adimi)
      let pool = product.keywordPool?.items;
      let insp = product.inspirations;
      if (isEtsy && !pool?.length) {
        setAiStatus("Etsy'de benzer listeler aranıyor, anahtar kelime havuzu ve ilham listesi kuruluyor (1-2 dk)…");
        const pr = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${categoryId}/prepare-seo`, { method: "POST", headers: authHeaders() });
        const pb = await pr.json().catch(() => ({}));
        if (!pr.ok) throw new Error(pb.error || "Anahtar kelime havuzu kurulamadı");
        pool = pb.pool?.items; insp = pb.inspirations;
        setCatalog((c) => c && ({ ...c, categories: (c.categories as ProductCategory[]).map((x) => x.id === product.id ? { ...x, keywordPool: pb.pool, inspirations: pb.inspirations } : x) }) as typeof c);
      }
      setAiStatus("Başlık, açıklama ve etiketler yazılıyor…");
      const src = product.source;
      const r = await fetch(`${getBackendUrl()}/api/listing-content/seo`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          marketplace,
          productName: product.name,
          brand: product.brand,
          sku: src?.sku,
          categoryPath: template?.category ?? undefined,
          colors,
          attributes: src?.attributes,
          supplierDescription: src?.descriptionText,
          inspirations: insp?.map((x) => ({ title: x.title, tags: x.tags })),
          keywordPool: pool,
          extra: aiExtra.trim() || undefined,
          templateId: template?.id,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Üretilemedi");
      setTitle(String(body.data?.title ?? "").slice(0, titleMax));
      // Etsy aciklamayi duz metin ister, Trendyol HTML gosterir
      const aiText = String((isEtsy ? body.data?.descriptionText : body.data?.descriptionHtml) ?? body.data?.descriptionText ?? "");
      // Sablonun alt metni AI girisinin altina eklenir (urunden doldurulur)
      const footerTpl = isEtsy ? template?.attributes?.descriptionFooter?.trim() : "";
      setDescription(footerTpl
        ? `${aiText.trim()}

${renderDescriptionFooter(footerTpl, product, buildVariations() as never, buildPersonalization() as never)}`
        : aiText);
      if (Array.isArray(body.data?.tags)) setTags(body.data.tags.slice(0, 13));
      if (Array.isArray(body.data?.materials) && body.data.materials.length) setAiMaterials(body.data.materials.slice(0, 13));
    } catch (e) {
      setError(e instanceof Error ? e.message : "İçerik üretilemedi");
    } finally {
      setAiBusy(false); setAiStatus("");
    }
  }

  /** Secili konseptler icin sirayla gorsel uretir ve listeye ekler.
   *  Sirayla: model istekleri agir, paralel gonderince zaman asimi aliyor. */
  async function generateShots() {
    const src = product?.source;
    const productImage = src?.images?.[0];
    if (!productImage || pickedConcepts.length === 0) return;
    setShotBusy(true); setError(null);
    let done = 0;
    for (const id of pickedConcepts) {
      const concept = concepts.find((c) => c.id === id);
      if (!concept) continue;
      setShotLog(`${done + 1}/${pickedConcepts.length} — ${concept.label || "konsept"} üretiliyor…`);
      try {
        const r = await fetch(`${getBackendUrl()}/api/media/generate-concept-shot`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            conceptImageUrl: concept.imageUrl,
            productImageUrl: productImage,
            productHint: product?.name,
            // Gorsel urunun galerisine de yazilir: baska listede tekrar uretilmez
            conceptId: concept.id,
            catalogId,
            categoryId,
          }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || "Görsel üretilemedi");
        const url = body.data?.imageUrl as string | undefined;
        if (url) setImages((prev) => (prev.includes(url) ? prev : [...prev, url]));
        done++;
      } catch (e) {
        setError(`${concept.label || "Konsept"}: ${e instanceof Error ? e.message : "üretilemedi"}`);
      }
    }
    setShotLog(done ? `${done} görsel üretildi` : "");
    setPickedConcepts([]);
    setShotBusy(false);
  }

  // ── Tarifin acilimi: urun + sablon → Etsy yuvalari ──
  //
  // 1. yuva: urunun FIYAT ekseni (kac halka, kac tas, hangi stil) — fiyat
  //          yalnizca buna bagli, boylece kombinasyon tavani 2500 kalir
  // 2-3.   : sablondaki "varyasyon" rollu setler (renk, zincir uzunlugu)
  // kalan  : kisisellestirme sorulari (isim, dogum tasi, zincir tipi…)
  const axisRoles = (product as unknown as { axisRoles?: Record<string, string> } | null)?.axisRoles ?? {};
  const productAxes = (product?.source?.variationTypes ?? []) as Array<{ name: string; values: string[] }>;
  const priceAxis = productAxes.find((a) => axisRoles[a.name] === "price") ?? null;

  // Secenekler sablondan gelir ama BU LISTEYE ozel degistirilebilir: bazen tek
  // listede fazladan bir secenek gerekiyor ya da biri cikarilmali.
  // Urune tanimli secenekler once; yoksa sablonunkiler
  const templateRefs = effectiveOptionRefs(product, template);
  const [listingRefs, setListingRefs] = useState<OptionRef[] | null>(null);
  const [refsFromTemplate, setRefsFromTemplate] = useState<string>("");
  useEffect(() => {
    const sig = JSON.stringify(templateRefs);
    if (sig !== refsFromTemplate) {
      setListingRefs(templateRefs);
      setRefsFromTemplate(sig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(templateRefs)]);

  const optionRefs = listingRefs ?? templateRefs;
  const { variationSets, personalizationSets, chartImages } = resolveOptionSets(optionRefs, optionSets, product);

  const buildVariations = () => buildVariationsLib({ product, cost, shipping, rule, variationSets, colors });
  // Ucretli ek (arka yuz): satis fiyati farki kadar ek ucret
  const buildPersonalization = () => buildPersonalizationLib(product, personalizationSets,
    // Etsy: liste fiyati gibi x2 (%50 kampanya ek ucrete de uygulanirsa satis farki kalir)
    (extra) => Math.max(0.2, Math.round((rule.price(cost + shipping + extra) - salePrice) * (isEtsy ? LIST_PRICE_FACTOR : 1) * 100) / 100));

  // Uretilen varyasyonlar DUZENLENEBILIR: etiket, fiyat ve hangi degerlerin
  // listede yer alacagi kullanicinin elinde. Urun/sablon degisince taban
  // yeniden hesaplanir ve duzenleme sifirlanir (imza karsilastirmasiyla).
  const basePlanned = buildVariations();
  const planSignature = JSON.stringify(basePlanned.map((v) => [v.name, v.options.map((o) => o.label)]));
  const [editedVariations, setEditedVariations] = useState<PlannedVariation[]>(basePlanned);
  const [editedSignature, setEditedSignature] = useState(planSignature);
  const [planOpen, setPlanOpen] = useState(false);
  useEffect(() => {
    if (planSignature !== editedSignature) {
      setEditedVariations(basePlanned);
      setEditedSignature(planSignature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planSignature]);

  // Kaydedilecek olan: yalnizca isaretli (dahil) degerler
  const plannedVariations = editedVariations
    .map((v) => ({ ...v, options: v.options.filter((o) => o.included !== false) }))
    .filter((v) => v.options.length > 0);
  const plannedCombos = plannedVariations.reduce((n, v) => n * v.options.length, 1);
  const plannedQuestions = buildPersonalization()?.questions ?? [];
  const entryVariant = template?.attributes?.entryVariant ?? null;
  const entryCombos = isEtsy ? entryComboPrices(plannedVariations, salePrice, rule, entryVariant) : [];

  // Secenek setlerinin chart gorselleri (zincir cetveli, font kartelasi…)
  // liste gorsellerinin SONUNA eklenir — Etsy'de aciklayici gorsel olarak.
  // Secenek seti chart'lari (urunde o secenek varsa) + sablonun ortak gorselleri
  const trailing = trailingImages(template, chartImages);
  useEffect(() => {
    if (trailing.length === 0) return;
    setImages((prev) => {
      const missing = trailing.filter((u) => !prev.includes(u));
      return missing.length ? [...prev, ...missing].slice(0, 10) : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailing.join("|")]);


  function updateOption(vi: number, oi: number, patch: Partial<PlannedOption>) {
    setEditedVariations((prev) => prev.map((v, i) => (i !== vi ? v : {
      ...v,
      options: v.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)),
    })));
  }

  async function saveDraft() {
    if (!product || missing.length) return;
    const limitErr = etsyLimitError(plannedVariations, plannedQuestions.length);
    if (limitErr) { setError(limitErr); return; }
    setSaving(true);
    try {
      const src = product.source;
      const draft = await apiClient.createListingDraft({
        // Urun fotograflarindan video (sunucuda arka planda, taslaga yazilir)
        ...(isEtsy ? { video: { aspectRatio: "1:1" as const, duration: 8 as const } } : {}),
        title: title.trim().slice(0, titleMax),
        description: description.trim(),
        tags,
        // Etsy'de %50 indirim yapilacak: liste fiyati x2 gonderilir
        price: salePrice > 0 ? (isEtsy ? Math.round(salePrice * LIST_PRICE_FACTOR * 100) / 100 : salePrice) : undefined,
        quantity,
        imageUrls: images,
        materials: aiMaterials ?? productMaterials(product),
        sku: src?.sku ?? null,
        category: template?.category ?? null,
        templateId: template?.id,
        ...(isEtsy && etsyTaxonomyId ? { taxonomyId: etsyTaxonomyId } : {}),
        // Tarifin acilimi: fiyat ekseni + sablondaki varyasyon setleri
        variations: isEtsy ? scaleVariationPrices(plannedVariations, LIST_PRICE_FACTOR) : plannedVariations,
        personalization: buildPersonalization(),
        attributes: {
          marketplace,
          // Sablonun en ucuz varyasyonu: kombinasyon basina (liste) fiyat
          ...(isEtsy && entryCombos.length ? { comboPrices: entryCombos.map((c) => ({ match: c.match, price: Math.round(c.price * LIST_PRICE_FACTOR * 100) / 100 })) } : {}),
          trendyol: isEtsy ? undefined : {
            categoryId: template?.trendyolCategoryId ?? null,
            categoryPath: template?.category ?? null,
            attributes: attrs,
            // Renk basina ozellik seti — Trendyol'da her barkod kendi
            // varyasyon degerleriyle acilir
            variants: colors.map((c) => ({
              color: c,
              stockCode: `${src?.sku ?? product.id}-${c.toUpperCase().replace(/\s+/g, "")}`,
              attributes: attrsForColor(c),
            })),
          },
          ...(isEtsy && templateTaxProps ? {
            etsyProperties: baseProps(templateTaxProps, {
              product, materials: aiMaterials ?? productMaterials(product),
              hasPersonalization: (buildPersonalization()?.questions.length ?? 0) > 0,
              variations: plannedVariations, colors,
            }, effectiveTemplateDefaults(template?.attributes, true)),
          } : {}),
          supplier: {
            catalogId, categoryId,
            sku: src?.sku ?? null,
            cost, shipping, currency,
            productUrl: src?.url ?? null,
          },
          // Etsy: taslaktaki fiyat liste fiyatidir (satis x2) — toplu fiyat guncellemesi bunu okur
          pricing: isEtsy
            ? {
                marginPct, salePrice, profit: Math.round(profit * 100) / 100,
                etsyFees: Math.round((etsyAnalysis?.fees.total ?? 0) * 100) / 100,
                listPriceFactor: LIST_PRICE_FACTOR, listPrice: Math.round(salePrice * LIST_PRICE_FACTOR * 100) / 100,
                ...(entryVariant ? { entryVariant } : {}),
              }
            : { multiplier, commissionPercent: commission, salePrice, profit },
        },
      });
      setSavedId(draft.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Taslak kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-16 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h1 className="text-sm font-semibold text-amber-900">Ürün bulunamadı</h1>
          <p className="mt-1 text-sm text-amber-800">Bu sayfa Tedarikçi Katalogları'ndaki bir ürünle açılır.</p>
          <Link to="/supplier-catalogs" className="mt-3 inline-block text-sm text-indigo-600 hover:underline">
            Tedarikçi Katalogları'na dön
          </Link>
        </div>
      </div>
    );
  }

  const src = product.source;

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <Link to="/supplier-catalogs" className="mb-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-3 w-3" /> Tedarikçi Katalogları
          </Link>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Package className="h-5 w-5 text-orange-600" /> Listing Builder
            <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${isEtsy ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>
              {isEtsy ? "Etsy" : "Trendyol"}
            </span>
          </h1>
          <p className="mt-1 text-sm text-gray-500">{product.name} · {src?.sku ?? "SKU yok"}</p>
        </div>
        <button
          onClick={saveDraft}
          disabled={saving || missing.length > 0}
          title={missing.length ? `Eksik: ${missing.join(", ")}` : "Taslağı kaydet"}
          className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Taslağı Kaydet
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {savedId && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <Check className="h-4 w-4" /> Taslak kaydedildi.
          <Link to={`/listing-drafts/${savedId}`} className="underline">Taslağı aç</Link>
          {isEtsy && (
            <>
              <span className="text-emerald-600">·</span>
              {/* Etsy'de yayin icin taksonomi, kargo profili ve isleme profili
                  gerekiyor; bunlarin ekrani Etsy Listing Builder'da. Taslak
                  oradan acilir ve dogrudan yayinlanir. */}
              <Link
                to={`/listing-builder?id=${savedId}`}
                className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
              >
                Etsy Listing Builder'da aç (yayınla)
              </Link>
            </>
          )}
        </div>
      )}
      {missing.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5" /> Yayına hazır değil — eksik: {missing.join(", ")}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* 1 · Urun + 2 · Sablon */}
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">1 · Ürün</h2>
          {/* Urune tiklayinca varyasyon/kisisellestirme duzenleyicisi acilir */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => isEtsy && setPlanOpen(true)}
            onKeyDown={(e) => { if (isEtsy && e.key === "Enter") setPlanOpen(true); }}
            title={isEtsy ? "Varyasyonları ve kişiselleştirmeyi düzenle" : undefined}
            className={`flex gap-3 rounded-lg ${isEtsy ? "cursor-pointer p-1 hover:bg-gray-50" : ""}`}
          >
            {src?.images?.[0] && <img src={abs(src.images[0])} alt="" className="h-20 w-20 rounded object-cover" />}
            <div className="min-w-0 text-xs text-gray-600">
              <p className="font-medium text-gray-900">{product.name}</p>
              <p className="mt-0.5">{product.brand}</p>
              <p className="mt-0.5">Maliyet: {cost.toFixed(2)} {currency}</p>
              {src?.url && (
                <a href={src.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                  Tedarikçide aç ↗
                </a>
              )}
            </div>
          </div>

          {src?.attributes && Object.keys(src.attributes).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {Object.entries(src.attributes).map(([k, v]) => (
                <span key={k} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                  {k}: <span className="text-gray-800">{v}</span>
                </span>
              ))}
            </div>
          )}

          <h2 className="mb-2 mt-5 text-sm font-semibold text-gray-900">2 · Liste şablonu</h2>
          <select
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              const t = templates.find((x) => x.id === e.target.value);
              if (t) applyTemplate(t);
            }}
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">Şablon seç…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.category ? ` — ${t.category}` : ""}</option>
            ))}
          </select>
          {templates.length === 0 && (
            <p className="mt-2 text-[11px] text-gray-500">
              {isEtsy ? "Liste şablonu yok." : "Trendyol şablonu yok."}{" "}
              <Link to="/templates" className="text-indigo-600 hover:underline">Liste Şablonları</Link>{" "}
              sayfasından {isEtsy ? "bir liste şablonu" : "kategori ve zorunlu özellikleri olan bir şablon"} oluştur.
            </p>
          )}
          {template && (
            <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50 p-2 text-[11px] text-gray-600">
              <p>Kategori: <span className="text-gray-900">{template.category ?? template.trendyolCategoryId}</span></p>
              <p className="mt-0.5">{attrs.length} özellik şablondan geldi</p>
            </div>
          )}
        </section>

        {/* 3 · Icerik */}
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">3 · İçerik</h2>
          <label className="block text-[11px] font-medium text-gray-600">
            Ürün adı
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, titleMax))}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
            />
          </label>
          <p className={`mt-0.5 text-[10px] ${title.length >= titleMax ? "text-red-600" : "text-gray-400"}`}>
            {title.length}/{titleMax} karakter
          </p>
          {isEtsy && titleWarnings(title).map((w) => (
            <p key={w} className="mt-0.5 text-[10px] text-amber-700">{w}</p>
          ))}

          <label className="mt-3 block text-[11px] font-medium text-gray-600">
            Açıklama
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={10}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 font-mono text-[11px]"
            />
          </label>
          <div className="mt-2 rounded-lg border border-pink-100 bg-pink-50/50 p-2">
            <div className="flex items-center gap-2">
              <input
                value={aiExtra}
                onChange={(e) => setAiExtra(e.target.value)}
                placeholder="Ek talimat (ops.): hediye vurgusu yap"
                className="min-w-0 flex-1 rounded border border-pink-200 bg-white px-2 py-1 text-[11px]"
              />
              <button
                type="button"
                onClick={generateContent}
                disabled={aiBusy}
                title={`Tedarikçi açıklamasından ${isEtsy ? "Etsy" : "Trendyol"} başlığı ve açıklaması üret`}
                className="flex items-center gap-1 rounded-lg bg-pink-600 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-pink-700 disabled:opacity-50"
              >
                {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                AI ile yaz
              </button>
            </div>
            <p className="mt-1 text-[10px] text-gray-500">
              {aiStatus || `Mevcut başlık ve açıklamanın üzerine yazar. Kaynak: tedarikçi açıklaması${isEtsy ? `, anahtar kelime havuzu (${product.keywordPool?.items.length ?? "yoksa önce kurulur"}) ve ilham listeleri (${product.inspirations?.length ?? 0})` : " + sabit özellikler"}.`}
            </p>
          </div>

          {/* Urunden gelen alanlar: renk her varyantta, beden tek deger.
              Etsy'de kategori ozelligi kavrami yok — blok gizlenir. */}
          {!isEtsy && Object.keys(sourceMap).length > 0 && (
            <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-2">
              <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-indigo-800">
                <Tag className="h-3 w-3" /> Üründen gelen alanlar
                {catAttrsLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              </p>

              {colorAttrs.map((a) => (
                <div key={a.id} className="mb-2">
                  <p className="text-[10px] text-gray-600">{a.name} — renk başına</p>
                  {a.values.length === 0 ? (
                    <p className="text-[10px] text-gray-500">Serbest metin: renk adı olduğu gibi gönderilir</p>
                  ) : (
                    colors.map((c) => (
                      <div key={c} className="mt-1 flex items-center gap-2">
                        <span className="w-16 shrink-0 text-[10px] text-gray-700">{c}</span>
                        <select
                          value={colorValues[c] ?? ""}
                          onChange={(e) => setColorValues((p) => ({ ...p, [c]: e.target.value ? Number(e.target.value) : "" }))}
                          className={`min-w-0 flex-1 rounded border px-1 py-0.5 text-[11px] ${
                            colorValues[c] ? "border-gray-200" : "border-red-300 bg-red-50"
                          }`}
                        >
                          <option value="">Seçiniz</option>
                          {a.values.map((v) => <option key={v.id ?? v.name} value={v.id ?? ""}>{v.name}</option>)}
                        </select>
                      </div>
                    ))
                  )}
                </div>
              ))}

              {sizeAttr && (
                <div className="mb-1">
                  <p className="text-[10px] text-gray-600">
                    {sizeAttr.name} — takıda beden yok, ürün için tek değer seçilir
                    {sizeCandidates[0] ? ` (aday: ${sizeCandidates.join(", ")})` : ""}
                  </p>
                  <select
                    value={sizeValue}
                    onChange={(e) => setSizeValue(e.target.value)}
                    className={`mt-0.5 w-full rounded border px-1 py-0.5 text-[11px] ${
                      sizeValue ? "border-gray-200" : "border-red-300 bg-red-50"
                    }`}
                  >
                    <option value="">Seçiniz</option>
                    {sizeAttr.values.map((v) => <option key={v.id ?? v.name} value={v.id ?? ""}>{v.name}</option>)}
                  </select>
                </div>
              )}

              {materialAttr && (
                <p className="text-[10px] text-gray-600">
                  {materialAttr.name}: <span className="text-gray-900">{materialText || "üründe yok"}</span>
                </p>
              )}
            </div>
          )}

          {/* Etsy etiketleri: 13 adet, arama buradan besleniyor */}
          {isEtsy && (
            <div className="mt-3">
              <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-gray-600">
                <Tag className="h-3 w-3" /> Etiketler ({tags.length}/13)
              </p>
              <div className="flex flex-wrap gap-1">
                {tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
                    title="Etiketi çıkar"
                    className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] text-gray-700 hover:border-red-200 hover:text-red-600"
                  >
                    {t} ✕
                  </button>
                ))}
              </div>
              <input
                placeholder="etiket ekle + Enter"
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const v = e.currentTarget.value.trim().toLowerCase().slice(0, 20);
                  if (!v || tags.includes(v) || tags.length >= 13) return;
                  setTags((prev) => [...prev, v]);
                  e.currentTarget.value = "";
                }}
                className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-[11px]"
              />
              {/* Anahtar kelime havuzu (agirlik sirasiyla, 20 karaktere sigan) + ilham listelerinin etiketleri: tiklayinca eklenir */}
              {(() => {
                const pool = Array.from(new Set([
                  ...(product?.keywordPool?.items ?? []).map((x) => x.phrase).filter((t) => t.length <= 20),
                  ...(product?.inspirations ?? []).flatMap((x) => x.tags.map((t) => t.toLowerCase().slice(0, 20))),
                ])).filter((t) => !tags.includes(t));
                if (!pool.length) return null;
                return (
                  <div className="mt-2">
                    <p className="mb-1 text-[10px] text-gray-500">{product?.keywordPool?.items.length ? "Anahtar kelime havuzu ve ilham etiketleri" : "İlham listelerinin etiketleri"} — tıkla, ekle</p>
                    <div className="flex flex-wrap gap-1">
                      {pool.map((t) => (
                        <button key={t} type="button" disabled={tags.length >= 13}
                          onClick={() => setTags((prev) => (prev.length >= 13 || prev.includes(t) ? prev : [...prev, t]))}
                          className="rounded-full border border-dashed border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-800 hover:bg-amber-100 disabled:opacity-40">
                          + {t}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {!isEtsy && attrs.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-gray-600">
                <Tag className="h-3 w-3" /> Şablondan sabit özellikler
              </p>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {attrs.map((a) => {
                  // Sablon yalnizca valueId saklıyor; okunur ad kategori
                  // listesinden cozulur, cozulemezse id gosterilir
                  const cat = catAttrs.find((c) => c.id === a.attributeId);
                  const name = a.customAttributeValue
                    ?? cat?.values.find((v) => v.id === a.attributeValueId)?.name
                    ?? (a.attributeValueId != null ? `#${a.attributeValueId}` : "—");
                  return (
                    <div key={a.attributeId} className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 text-[11px]">
                      <span className="text-gray-600">{cat?.name ?? a.attributeName}</span>
                      <span className="text-gray-900">{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* 4 · Gorseller + 5 · Fiyat */}
        <section className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <ImageIcon className="h-4 w-4" /> 4 · Görseller ({images.length})
            </h2>
            <div className="flex flex-wrap gap-2">
              {images.map((u, i) => (
                <div key={u} className="relative">
                  <img src={abs(u)} alt="" className="h-16 w-16 rounded border border-gray-200 object-cover" />
                  {i === 0 && (
                    <span className="absolute left-0 top-0 rounded-br bg-gray-900/70 px-1 text-[8px] text-white">kapak</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setImages((prev) => prev.filter((x) => x !== u))}
                    title="Görseli çıkar"
                    className="absolute -right-1 -top-1 rounded-full bg-white px-1 text-[9px] text-red-600 shadow"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            {/* Tarifin acilimi — ozet. Ayrintili duzenleme buyuk modalda:
                dar panelde deger listeleri ve fiyatlar okunmuyordu. */}
            {isEtsy && (editedVariations.length > 0 || plannedQuestions.length > 0) && (
              <button
                onClick={() => setPlanOpen(true)}
                className="mt-3 w-full rounded-lg border border-indigo-200 bg-indigo-50/60 p-2 text-left transition-colors hover:bg-indigo-100/60"
              >
                <p className="mb-0.5 text-[11px] font-medium text-indigo-800">
                  Etsy yuvaları — düzenlemek için tıkla
                </p>
                <p className="text-[11px] text-gray-700">
                  {editedVariations.length > 0
                    ? editedVariations.map((v, i) => `${i + 1}. ${v.name} (${v.options.filter((o) => o.included !== false).length})`).join(" · ")
                    : "Varyasyon yok — tek fiyatlı liste"}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {plannedCombos} kombinasyon
                  {plannedQuestions.length > 0 && ` · ${plannedQuestions.length} kişiselleştirme alanı`}
                  {plannedCombos > 2500 && " · SINIR AŞILDI"}
                </p>
              </button>
            )}

            {/* Konsept fotograflari: sahne sablonu sec, urun sahneye yerlessin */}
            <div className="mt-3 rounded-lg border border-pink-100 bg-pink-50/40 p-2">
              <p className="mb-1 text-[11px] font-medium text-pink-800">
                Konsept fotoğrafı üret ({pickedConcepts.length} seçili)
              </p>
              {/* Sahne seti: tarifteki koleksiyon ya da elle secim. Bir grubun
                  (kolye, yuzuk...) konseptleri tek tikla isaretlenir. */}
              {conceptCollections.length > 0 && (
                <div className="mb-2 flex items-center gap-1.5">
                  <select
                    value=""
                    onChange={(e) => {
                      const col = conceptCollections.find((c) => c.id === e.target.value);
                      if (!col) return;
                      const ids = col.mockups.map((m) => m.id).filter((id) => concepts.some((c) => c.id === id));
                      setPickedConcepts(ids);
                    }}
                    className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]"
                  >
                    <option value="">Sahne seti seç…</option>
                    {conceptCollections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.mockups.length})
                      </option>
                    ))}
                  </select>
                  {pickedConcepts.length > 0 && (
                    <button onClick={() => setPickedConcepts([])} className="text-[11px] text-gray-500 hover:text-gray-700 underline">
                      Temizle
                    </button>
                  )}
              {concepts.length === 0 ? (
                <p className="text-[10px] text-gray-500">
                  Konsept fotoğrafı yok.{" "}
                  <Link to="/mockup-manager" className="text-indigo-600 hover:underline">Konsept Fotoğrafları</Link>{" "}
                  sayfasından yükle.
                </p>
              ) : (
                <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                </div>
              )}
                  {concepts.map((c) => {
                    const on = pickedConcepts.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setPickedConcepts((prev) =>
                          prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id])}
                        title={c.label || ""}
                        className={`h-12 w-12 overflow-hidden rounded border-2 ${on ? "border-pink-500" : "border-transparent"}`}
                      >
                        <img src={abs(c.thumbnailUrl || c.imageUrl)} alt="" className="h-full w-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={generateShots}
                disabled={shotBusy || pickedConcepts.length === 0 || !product?.source?.images?.length}
                title={!product?.source?.images?.length ? "Ürünün fotoğrafı yok" : "Seçili konseptlerde ürünü göster"}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-pink-600 px-3 py-2 text-xs font-medium text-white hover:bg-pink-700 disabled:opacity-50"
              >
                {shotBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Üret
              </button>
              {shotLog && <p className="mt-1 text-[10px] text-gray-600">{shotLog}</p>}
              <p className="mt-1 text-[10px] text-gray-400">
                Sahne korunur, yalnızca takı ürünle değiştirilir. Üretilenler yukarıdaki listeye eklenir.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-900">5 · Fiyat ve stok</h2>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {isEtsy ? (
                <label className="col-span-2 text-gray-600" title="Tüm Etsy ücretleri düşüldükten sonra satış fiyatının bu yüzdesi net kâr kalır">
                  Hedef kâr %
                  <input type="number" step="1" min="0" max="70" value={marginPct}
                    onChange={(e) => {
                      const v = Math.min(70, Math.max(0, Number(e.target.value) || 0));
                      setMarginPct(v);
                      if (v > 0) writeLastMargin(v);
                    }}
                    className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-sm" />
                </label>
              ) : (<>
                <label className="text-gray-600">
                  Çarpan
                  <input type="number" step="0.1" min="1" value={multiplier}
                    onChange={(e) => setMultiplier(Number(e.target.value) || 1)}
                    className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-sm" />
                </label>
                <label className="text-gray-600">
                  Komisyon %
                  <input type="number" step="1" min="0" value={commission}
                    onChange={(e) => setCommission(Number(e.target.value) || 0)}
                    className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-sm" />
                </label>
              </>)}
              <label className="text-gray-600">
                Stok
                <input type="number" step="1" min="1" value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value) || 1)}
                  className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-sm" />
              </label>
              {isEtsy ? (
                <label className="text-gray-600" title={`Ürüne kaydedilir. Boş: varsayılan ${DEFAULT_ETSY_SHIPPING_USD} USD`}>
                  Kargo (USD)
                  <input type="number" step="0.1" min="0"
                    value={shipEdit ?? (product?.shippingCostOverride != null ? String(product.shippingCostOverride) : "")}
                    placeholder={String(DEFAULT_ETSY_SHIPPING_USD)}
                    onChange={(e) => setShipEdit(e.target.value)}
                    onBlur={() => void saveShipping()}
                    className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-sm" />
                </label>
              ) : (
                <div className="text-gray-600">
                  Kargo
                  <p className="mt-1 text-sm text-gray-900">{shipping ? shipping.toFixed(2) : "—"}</p>
                </div>
              )}
            </div>

            <div className="mt-3 space-y-1 rounded-lg bg-gray-50 p-2 text-[11px]">
              <div className="flex justify-between"><span className="text-gray-500">Maliyet</span><span>{cost.toFixed(2)} + kargo {shipping.toFixed(2)} = {(cost + shipping).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Satış fiyatı</span><span className="font-semibold text-gray-900">{salePrice.toFixed(2)}</span></div>
              {etsyAnalysis && (
                <div className="flex justify-between text-gray-500"
                  title={`İşlem ${etsyAnalysis.fees.transaction.toFixed(2)} · Ödeme ${etsyAnalysis.fees.payment.toFixed(2)} · Listeleme ${etsyAnalysis.fees.listing.toFixed(2)} · Regulatory ${etsyAnalysis.fees.regulatory.toFixed(2)}`}>
                  <span>Etsy ücretleri</span><span>−{etsyAnalysis.fees.total.toFixed(2)}</span>
                </div>
              )}
              {isEtsy && (
                <div className="flex justify-between text-gray-500"><span>Etsy liste fiyatı (×{LIST_PRICE_FACTOR}, %50 indirim)</span><span>{(salePrice * LIST_PRICE_FACTOR).toFixed(2)}</span></div>
              )}
              {entryCombos.length > 0 && (
                <div className="flex justify-between text-amber-800"><span>En ucuz varyasyon ({entryCombos.length})</span><span>{Math.min(...entryCombos.map((c) => c.price)).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{etsyAnalysis ? "Net kâr / marj / maliyete" : "Kâr / marj"}</span>
                <span className={profit > 0 ? "text-emerald-700" : "text-red-600"}>
                  {profit.toFixed(2)} · %{margin.toFixed(1)}{etsyAnalysis ? ` · %${etsyAnalysis.markupPct.toFixed(0)}` : ""}
                </span>
              </div>
            </div>

            {colors.length > 0 && (
              <div className="mt-3">
                <p className="mb-1 text-[11px] font-medium text-gray-600">Renk varyantları ({colors.length})</p>
                <div className="flex flex-wrap gap-1">
                  {colors.map((c) => (
                    <span key={c} className="rounded-full border border-gray-200 px-2 py-0.5 text-[10px] text-gray-700">{c}</span>
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-gray-400">
                  Tek ürün, renk başına ayrı barkod (stockCode = SKU-RENK).
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {isEtsy && (
        <div className="mt-6">
          <EtsyListingPreviewBox defaultOpen title={title} images={images} chartImages={chartImages} price={salePrice}
            variations={plannedVariations} personalization={buildPersonalization()} />
        </div>
      )}

      {vertical !== "jewelry" && (
        <p className="mt-4 text-[11px] text-gray-400">
          Not: aktif mağaza takı dikeyinde değil — bu ekran takı ürünleri için tasarlandı.
        </p>
      )}
      {/* Varyasyon duzenleyici — buyuk modal. Urun basligina ya da ozet
          kutusuna tiklayinca acilir; her sey tek ekranda gorunur. */}
      {planOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setPlanOpen(false)}>
          <div className="my-6 w-full max-w-5xl rounded-xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                {product?.source?.images?.[0] && (
                  <img src={abs(product.source.images[0])} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
                )}
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">{product?.name}</h3>
                  <p className="text-[11px] text-gray-500">
                    {product?.source?.sku ? `${product.source.sku} · ` : ""}
                    {plannedCombos} kombinasyon
                    {plannedQuestions.length > 0 && ` · ${plannedQuestions.length} kişiselleştirme alanı`}
                  </p>
                </div>
              </div>
              <button onClick={() => setPlanOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            {plannedCombos > 2500 && (
              <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                {plannedCombos} kombinasyon — Etsy sınırı 2500. Değer çıkararak azaltın.
              </p>
            )}

            {/* Bu listenin secenekleri — sablondan gelir, burada degistirilebilir */}
            <div className="mb-4 rounded-xl border border-gray-200 p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-700">Seçenekler</span>
                <span className="text-[10px] text-gray-400">
                  şablondan gelir · bu listeye özel değişiklik yapabilirsin
                </span>
                {optionPresets.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const p = optionPresets.find((x) => x.id === e.target.value);
                      if (p) setListingRefs(p.refs ?? []);
                    }}
                    className="ml-auto rounded border border-gray-200 px-2 py-1 text-[11px]"
                  >
                    <option value="">Kalıptan uygula…</option>
                    {optionPresets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                <button
                  onClick={() => setListingRefs(templateRefs)}
                  className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
                >
                  Şablona dön
                </button>
              </div>

              <div className="space-y-1">
                {optionRefs.map((r, i) => {
                  const set = optionSets.find((o) => o.id === r.optionSetId);
                  return (
                    <div key={`${r.optionSetId}-${i}`} className="flex items-center gap-2 rounded border border-gray-100 px-2 py-1">
                      <span className="min-w-0 flex-1 truncate text-xs text-gray-800">
                        {set?.etsyName?.trim() || set?.name || "(silinmiş set)"}
                        {set && (
                          <span className="ml-1 text-[10px] text-gray-400">
                            {set.kind === "list" ? `${set.values.length} değer` : set.kind}
                          </span>
                        )}
                      </span>
                      {set?.kind === "chart" ? (
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700">yalnızca görsel</span>
                      ) : (
                        <select
                          value={r.role}
                          onChange={(e) => setListingRefs(optionRefs.map((x, j) => (j === i ? { ...x, role: e.target.value as OptionRef["role"] } : x)))}
                          className="rounded border border-gray-200 px-1.5 py-1 text-[11px]"
                        >
                          <option value="variation">Varyasyon</option>
                          <option value="personalization">Kişiselleştirme</option>
                        </select>
                      )}
                      {set?.kind !== "chart" && r.role === "personalization" && (
                        <label className="flex items-center gap-1 text-[10px] text-gray-500" title="Alıcı bu alanı doldurmadan sipariş veremez">
                          <input type="checkbox" checked={r.required !== false}
                            onChange={(e) => setListingRefs(optionRefs.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} />
                          zorunlu
                        </label>
                      )}
                      <button
                        onClick={() => setListingRefs(optionRefs.filter((_, j) => j !== i))}
                        className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
                {optionRefs.length === 0 && (
                  <p className="text-[11px] text-gray-400">Seçenek yok — aşağıdan ekleyebilirsin.</p>
                )}
              </div>

              <select
                value=""
                onChange={(e) => {
                  const set = optionSets.find((o) => o.id === e.target.value);
                  if (set) setListingRefs([...optionRefs, { optionSetId: set.id, role: set.defaultRole }]);
                }}
                className="mt-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
              >
                <option value="">Seçenek seti ekle…</option>
                {optionSets
                  .filter((o) => !optionRefs.some((r) => r.optionSetId === o.id))
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.etsyName ? ` (${o.etsyName})` : ""} — {o.kind === "list" ? `${o.values.length} değer` : o.kind}
                    </option>
                  ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {editedVariations.map((v, vi) => {
                const on = v.options.filter((o) => o.included !== false).length;
                return (
                  <div key={`${v.name}-${vi}`} className="rounded-xl border border-gray-200 p-3">
                    <div className="mb-2">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                          {vi + 1}. yuva{vi === 0 && priceAxis ? " · fiyat" : ""}
                        </span>
                        <span className="text-[10px] text-gray-400">{on}/{v.options.length} seçili</span>
                      </div>
                      <input
                        value={v.name}
                        onChange={(e) => setEditedVariations((prev) => prev.map((x, i) => (i === vi ? { ...x, name: e.target.value } : x)))}
                        className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm font-medium text-gray-900"
                      />
                    </div>

                    <div className="mb-1 flex gap-2 text-[10px] text-gray-400">
                      <button
                        onClick={() => setEditedVariations((prev) => prev.map((x, i) => (i === vi
                          ? { ...x, options: x.options.map((o) => ({ ...o, included: true })) } : x)))}
                        className="hover:text-indigo-600"
                      >
                        tümünü seç
                      </button>
                      <button
                        onClick={() => setEditedVariations((prev) => prev.map((x, i) => (i === vi
                          ? { ...x, options: x.options.map((o) => ({ ...o, included: false })) } : x)))}
                        className="hover:text-indigo-600"
                      >
                        hiçbiri
                      </button>
                    </div>

                    <div className="max-h-80 space-y-1 overflow-y-auto">
                      {v.options.map((o, oi) => (
                        <div key={`${o.label}-${oi}`} className={`flex items-center gap-1.5 rounded px-1 py-0.5 ${o.included === false ? "opacity-40" : ""}`}>
                          <input
                            type="checkbox"
                            checked={o.included !== false}
                            onChange={(e) => updateOption(vi, oi, { included: e.target.checked })}
                          />
                          <input
                            value={o.label}
                            onChange={(e) => updateOption(vi, oi, { label: e.target.value })}
                            className="min-w-0 flex-1 rounded border border-gray-200 px-1.5 py-1 text-xs"
                          />
                          {o.sellingPrice != null ? (
                            <div className="flex shrink-0 items-center gap-0.5">
                              <span className="text-[10px] text-gray-400">$</span>
                              <input
                                type="number" step="0.01" min="0"
                                value={o.sellingPrice}
                                onChange={(e) => updateOption(vi, oi, { sellingPrice: Number(e.target.value) || 0 })}
                                className="w-16 rounded border border-gray-200 px-1 py-1 text-right text-xs"
                              />
                            </div>
                          ) : (
                            <span className="w-[68px] shrink-0 text-right text-[10px] text-gray-300">fiyatsız</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Kisisellestirme sorulari — burada salt okunur; kaynagi sablon */}
              {plannedQuestions.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                  <p className="mb-2 text-[11px] font-medium text-amber-800">
                    Kişiselleştirme ({plannedQuestions.length}/5)
                  </p>
                  <div className="space-y-1.5">
                    {plannedQuestions.map((q, i) => (
                      <div key={i} className="rounded border border-amber-100 bg-white px-2 py-1.5">
                        <p className="text-xs text-gray-800">{String(q.question_text)}</p>
                        <p className="text-[10px] text-gray-500">
                          {String(q.question_type)}
                          {q.options ? ` · ${(q.options as unknown[]).length} seçenek` : ""}
                          {q.max_allowed_characters ? ` · ${q.max_allowed_characters} karakter` : ""}
                          {q.required ? " · zorunlu" : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-amber-700">
                    Sorular şablondaki seçenek setlerinden ve ürünün isim alanlarından gelir.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-between gap-2">
              <button
                onClick={() => { setEditedVariations(basePlanned); }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
              >
                Varsayılana dön
              </button>
              <button
                onClick={() => setPlanOpen(false)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
